import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from "@angular/router";
import { Observable, of, Subject, throwError } from "rxjs";
import { catchError, finalize, takeUntil, tap } from "rxjs/operators";
import { SelectItem } from "primeng/api";

// Subjects / Services
import { CatalogService } from "../../../services/catalog.service";
import { MapsSubject } from "../../../subject/maps.subject";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { OptionResponseDto } from "../../../dto/option.reponse.dto";
import { PropertyLocationDto } from "../../../dto/property.location.dto";
import { ResponseDto } from "../../../dto/response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

declare const google: any;

const STEP_KEY = 'maps';

@Component({
    selector: 'app-property-maps',
    templateUrl: './property-maps.component.html',
    styleUrls: ['./property-maps.component.scss']
})
export class PropertyMapsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('mapElement', { static: false }) mapElement!: ElementRef;
    @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;

    // --- Flujo / navegación ---
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --- Estado general ---
    loading = false;
    private destroy$ = new Subject<void>();

    // --- Propiedad / backend ---
    propertyId: number | null | undefined;
    propertyLocationDto: PropertyLocationDto | null = null;
    segmentLabel: string | undefined = '';

    // --- Mapa & Ubicación ---
    map!: google.maps.Map;
    marker!: google.maps.Marker;
    circle!: google.maps.Circle;
    lastSelectedPosition: google.maps.LatLng | null | undefined = null;

// --- Mapa & Ubicación ---
    cityLocation: google.maps.LatLngLiteral | undefined = undefined;
    zoneLocation: google.maps.LatLngLiteral | undefined = undefined;

    // --- Opciones de visualización ---
    targetTypes: OptionResponseDto[] = [];
    selectedTargetType: string = 'Ubicación Exacta';
    selectedTargetTypeCode: string = '';

    // --- Error ---
    dialogMessage = '';
    dialogCode?: string;
    showErrorDialog = false;

    constructor(
        private router: Router,
        private catalogService: CatalogService,
        private propertyInitialSubject: PropertyInitialSubject,
        private mapsSubject: MapsSubject,
        private propertyService: PropertyService,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Estado global del paso
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) IDs y stage inicial
        const initial = this.propertyInitialSubject.getCurrentProperty();
        this.propertyId = initial?.propertyId ?? this.flowState.propertyId;
        if (initial?.recordStage) this.flowState.setRecordStage(initial.recordStage as RecordStageEnum);
        if (!this.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar data del backend y catálogos
        this.loadOptionsMaps();
        this.loadPropertyLocation(this.propertyId);

        // 5) Restaurar estado local si existe (posición y modo de visualización)
        const cached = this.flowState.getStepData(STEP_KEY);
        if (cached) {
            this.applyCache(cached);
        }
        console.log('Estado inicial del paso:', cached);
        console.log('Modo actual:', this.currentMode);
        console.log('ID de propiedad:', this.propertyId);
        console.log('Ruta anterior:', this.previousUrl);
        console.log('Ruta siguiente:', this.nextUrl);
        console.log('Opciones de visualización:', this.targetTypes);
    }

    ngAfterViewInit(): void {
        this.initMap();
        this.initAutocomplete();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Backend (siempre UPDATE para maps)
    // -----------------------------------------------------------
    private loadPropertyLocation(propertyId: number | null): void {
        if (!propertyId) return;
        this.propertyService.getPropertyLocation(propertyId).pipe(
            tap((response) => {
                this.segmentLabel = response.segmentLabel;
                this.propertyLocationDto = response;
                // geocode cuando el mapa esté inicializado
                if (this.map) {
                    this.geocodeAddress();
                } else {
                    // si aún no está listo, espera a AfterViewInit
                    setTimeout(() => this.geocodeAddress(), 0);
                }
            }),
            catchError((error) => {
                console.error('Error al cargar ubicación existente:', error);
                return of(null);
            })
        ).subscribe();
    }

    private loadOptionsMaps(): void {
        this.catalogService.getCatalogList('CT_MAP_AREA').pipe(
            tap((options: any) => {
                if (Array.isArray(options)) {
                    this.targetTypes = options;
                }
            }),
            catchError((error) => {
                console.error('Error al cargar opciones de mapa:', error);
                return of([]);
            })
        ).subscribe();
    }

    // -----------------------------------------------------------
    // Google Maps
    // -----------------------------------------------------------
    initMap(): void {
        const defaultLocation = { lat: -16.500000, lng: -68.150000 }; // La Paz
        this.map = new google.maps.Map(this.mapElement.nativeElement, {
            center: defaultLocation,
            zoom: 18,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
        });

        this.marker = new google.maps.Marker({
            position: defaultLocation,
            map: this.map,
            draggable: true,
            visible: false
        });

        this.circle = new google.maps.Circle({
            map: this.map,
            radius: 1000,
            visible: false
        });

        this.marker.addListener('dragend', () => {
            this.lastSelectedPosition = this.marker.getPosition();
            this.persistToFlowState();
        });

        // Si ya teníamos cache, reflejarlo
        const cached = this.flowState.getStepData(STEP_KEY);
        if (cached?.position) {
            this.updateMapDisplay(this.selectedTargetType, cached.position);
        }
    }

    initAutocomplete(): void {
        const autocomplete = new google.maps.places.Autocomplete(this.searchInput?.nativeElement, {
            types: ['geocode'],
            componentRestrictions: { country: 'BO' },
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place?.geometry?.location) {
                this.updateMapDisplay('Ubicación Exacta', place.geometry.location);
                this.lastSelectedPosition = place.geometry.location;
                this.persistToFlowState();
            }
        });
    }

    // Geocode the initial address based on property data
    geocodeAddress(): void {
        const geocoder = new google.maps.Geocoder();
        const fullAddress = `${this.propertyLocationDto?.mapSearch || ''}, Bolivia`;
        geocoder.geocode({ address: fullAddress }, (results: any, status: string) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
                this.cityLocation = results[0].geometry.location.toJSON();
                this.zoneLocation = results[0].geometry.location.toJSON();

                // Default to showing exact location on map
                this.updateMapDisplay('Ubicación Exacta', this.zoneLocation);
            } else {
                console.error('Geocode error:', status);
            }
        });
    }

    private getCityLocation(): google.maps.LatLngLiteral {
        return this.cityLocation || { lat: -16.500000, lng: -68.150000 };
    }
    private getZoneLocation(): google.maps.LatLngLiteral {
        return this.zoneLocation || { lat: -16.500000, lng: -68.150000 };
    }

    onDisplayOptionChange(option: OptionResponseDto): void {
        this.selectedTargetType = option.optionName;
        this.selectedTargetTypeCode = option.optionCode;

        if (this.selectedTargetType === 'Ubicación Exacta') {
            const exactLocation = this.lastSelectedPosition || this.getZoneLocation();
            this.updateMapDisplay('Ubicación Exacta', exactLocation);
        } else if (this.selectedTargetType === 'Área de la Zona') {
            const zoneLocation = this.getZoneLocation();
            this.updateMapDisplay('Área de la Zona', zoneLocation);
        } else if (this.selectedTargetType === 'Área de la Ciudad') {
            const cityLocation = this.getCityLocation();
            this.updateMapDisplay('Área de la Ciudad', cityLocation);
        }
        this.persistToFlowState();
    }

    private updateMapDisplay(mode: string, location?: google.maps.LatLng | google.maps.LatLngLiteral): void {
        const loc = (location as any) || { lat: -16.500000, lng: -68.150000 };

        if (mode === 'Ubicación Exacta') {
            this.marker.setVisible(true);
            this.marker.setPosition(loc);
            this.circle.setVisible(false);
            this.map.setCenter(loc);
            this.map.setZoom(18);
        } else if (mode === 'Área de la Zona') {
            this.marker.setVisible(false);
            this.circle.setVisible(true);
            this.circle.setCenter(loc);
            this.circle.setRadius(350);
            this.map.setCenter(loc);
            this.map.setZoom(16);
        } else if (mode === 'Área de la Ciudad') {
            this.marker.setVisible(false);
            this.circle.setVisible(false);
            this.map.setCenter(loc);
            this.map.setZoom(13);
        }
    }

    // -----------------------------------------------------------
    // Persistencia local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        const pos = this.lastSelectedPosition?.toJSON?.() ?? this.marker?.getPosition?.()?.toJSON?.() ?? null;
        this.flowState.setStepData(STEP_KEY, {
            selectedTargetType: this.selectedTargetType,
            selectedTargetTypeCode: this.selectedTargetTypeCode,
            position: pos // {lat, lng} o null
        });
    }

    private applyCache(cached: any): void {
        if (!cached) return;
        this.selectedTargetType = cached.selectedTargetType ?? this.selectedTargetType;
        this.selectedTargetTypeCode = cached.selectedTargetTypeCode ?? this.selectedTargetTypeCode;

        if (cached.position) {
            // si el mapa ya existe, actualiza inmediatamente
            if (this.map) {
                this.updateMapDisplay(this.selectedTargetType, cached.position);
            }
            // si no, AfterViewInit se encarga
        }
    }

    // -----------------------------------------------------------
    // Navegación + Guardado (siempre UPDATE para maps)
    // -----------------------------------------------------------
    handleNextStep(): void {
        this.saveSelectedLocation().subscribe({
            next: () => {
                if (this.shouldAdvanceStage()) {
                    this.nextStagePropertyService({ id: this.propertyId });
                } else {
                    this.router.navigate([this.nextUrl]);
                }
            },
            error: () => {}
        });
    }

    handleSaveAndExit(): void {
        this.saveSelectedLocation().subscribe({
            next: () => {
                // Guardar y salir → no avanzar etapa
                this.router.navigate(['/list-pending']); // o dashboard
            },
            error: () => {}
        });
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    // Política para avanzar etapa:
    // - CREATE → avanza
    // - RESUME → avanza sólo si estás en un paso posterior al recordStage
    // - EDIT → no avanza
    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        // RESUME
        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx; // estamos después → avanzamos
    }

    // **Siempre UPDATE** (para todos los modos)
    saveSelectedLocation(): Observable<any> {
        const pos = this.lastSelectedPosition || this.marker?.getPosition?.();
        if (!pos || !this.propertyId) {
            this.dialogDisplayError('Debe seleccionar una ubicación en el mapa.', '400');
            return throwError(() => new Error('No position selected or no propertyId'));
        }

        const locationData = {
            latitude: pos.lat().toString(),
            longitude: pos.lng().toString(),
            ctMapArea: this.selectedTargetTypeCode
        };

        // cachea siempre antes
        this.persistToFlowState();

        this.loading = true;
        return this.propertyService.updatePropertyLocation(this.propertyId, locationData).pipe(
            tap(() => {
                // opcional: actualizar recordStage local a maps si tu backend no lo hace
                this.flowState.setRecordStage(RecordStageEnum.TO_REGISTER_LOCATION);
            }),
            finalize(() => (this.loading = false)),
            catchError((error) => {
                this.responseHandler(error);
                return throwError(() => new Error('Error al registrar la ubicación'));
            })
        );
    }

    // -----------------------------------------------------------
    // Errores
    // -----------------------------------------------------------
    responseHandler(responseDto: ResponseDto | any) {
        const error = responseDto?.errorDetail || responseDto?.message || 'Error inesperado';
        const status = responseDto?.statusCode || responseDto?.status || '500';
        this.dialogDisplayError(error, status);
    }

    dialogDisplayError(error: string | null, status: string) {
        this.showErrorDialog = true;
        this.dialogMessage = error ?? '';
        this.dialogCode = status;
    }

    // Avanzar etapa en backend
    nextStagePropertyService(propertyId: any): void {
        this.propertyService.nextStageProperty(propertyId).pipe(
            tap(() => this.router.navigate([this.nextUrl])),
            catchError((error) => {
                console.error('Error al avanzar al siguiente estado:', error);
                // fallback: navegar igual para no bloquear UX
                this.router.navigate([this.nextUrl]);
                return of(null);
            })
        ).subscribe();
    }
}
